import { scoreFromStats, pprLabel } from "./projlabProjection";

// Projections → personal draft values (VORP-based auction dollars).
//
// This is the ONE place projlab output becomes prices the user drafts against.
// It is deliberately isolated from the AI engine: it imports NOTHING from
// src/engine and never reads or writes TIER_MAPS. The AI stays Smithtown-
// calibrated; these values are the user's private "what's this worth to ME
// under my league's scoring" number, surfaced as a My Value / delta column.
//
// Stats are the source of truth; points are a lens (scoreFromStats). Switching
// leagues re-scores and re-prices without touching any projection seed.

const STARTER_POS = ["QB", "RB", "WR", "TE"];

// Suffix-stripped keys, matching findPlayerHist's normalization in
// projlabProjection.js — keep this regex identical to that one so a value
// keyed here resolves against the same name the rest of projlab uses.
const SUFFIX_RE = /\s+(Jr\.?|Sr\.?|II|III|IV|V)$/i;
const stripSuffix = (name) => (name || "").replace(SUFFIX_RE, "").trim();
export const draftValueKey = (name, pos) => `${stripSuffix(name)}-${pos}`;

// Per-league localStorage key for the "Build Draft Values" snapshot, so building
// values in one league never silently overwrites another's. Mirrors the
// per-league tier-override keying (userTierSheet_<league>); null league → a
// shared "__default__" bucket. Both writer (ProjectionLab) and reader
// (PlayerList) import this so the slug can never drift.
export const draftValuesStorageKey = (league) => {
  const slug = league
    ? league.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
    : "";
  return `mock1_draft_values_2026_${slug || "__default__"}`;
};

// Greedily fill `count` flex slots from the eligible position pools, always
// taking the highest-scoring player not yet drafted at any eligible position.
// Mutates `taken` (per-pos drafted count). Pools must be pts-desc sorted.
function allocFlex(count, eligible, pools, taken) {
  for (let i = 0; i < count; i++) {
    let bestPos = null, bestPts = -Infinity;
    for (const pos of eligible) {
      const next = pools[pos]?.[taken[pos]];
      if (next && next.pts > bestPts) { bestPts = next.pts; bestPos = pos; }
    }
    if (!bestPos) break; // pools exhausted
    taken[bestPos]++;
  }
}

// field: composite projlab field entries { player, pos, team, stats, ... }.
// settings: { teams, budget, positions:{QB,RB,WR,TE,FLEX,SFLEX,...,BN}, scoring }.
// Returns { list, values, meta }:
//   list   — rich rows { key, name, pos, team, pts, vorp, value }, value-desc.
//   values — plain { key: dollars } lookup for snapshot/export.
//   meta   — { teams, budget, rosterSize, pool, scoringLabel, replacement, taken, totalVorp }.
//            `taken` is demand per position — teams × positions[pos] plus the
//            greedily-allocated FLEX/SFLEX (and bench, under benchDemand),
//            capped at pool size. It's the exact count behind each replacement
//            level, exposed so consumers don't have to re-derive it.
// Unprojected players, K, and DEF are not in `values`; consumers default them
// to $1 (K/DEF fixed $1, unprojected $1).
// options.benchDemand (default false): when true, demand additionally greedy-
// fills teams × positions.BN bench slots from the best remaining QB/RB/WR/TE,
// deepening replacement levels (more players count as "rostered", so the last
// drafted at each position sits lower and VORP spreads toward mid-tier).
export function buildDraftValues(field, settings, options = {}) {
  const { teams, budget, positions, scoring } = settings;
  // rosterSize is settings-derived (sum of ALL slots incl. BN) — never the
  // MOCK_ROSTER_SIZE hardcode, so 12/14-team and non-standard rosters price right.
  const rosterSize = Object.values(positions).reduce((s, v) => s + (v || 0), 0);
  const pool = teams * (budget - rosterSize);

  // 1. Score every projected skill player under the league lens.
  const scored = (field || [])
    .filter((p) => p.stats && STARTER_POS.includes(p.pos))
    .map((p) => ({
      name: p.player,
      pos: p.pos,
      team: p.team,
      pts: scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17),
    }));

  // 2. Replacement levels — demand = teams × positions[pos], FLEX/SFLEX filled
  //    greedily; replacement[pos] = pts of the LAST drafted starter at that pos.
  const pools = {};
  const taken = {};
  for (const pos of STARTER_POS) {
    pools[pos] = scored.filter((p) => p.pos === pos).sort((a, b) => b.pts - a.pts);
    taken[pos] = Math.min(teams * (positions[pos] || 0), pools[pos].length);
  }
  allocFlex(teams * (positions.FLEX || 0), ["RB", "WR", "TE"], pools, taken);
  allocFlex(teams * (positions.SFLEX || 0), ["QB", "RB", "WR", "TE"], pools, taken);
  if (options.benchDemand) {
    allocFlex(teams * (positions.BN || 0), ["QB", "RB", "WR", "TE"], pools, taken);
  }

  const replacement = {};
  for (const pos of STARTER_POS) {
    const t = taken[pos];
    replacement[pos] = t > 0 && pools[pos][t - 1] ? pools[pos][t - 1].pts : 0;
  }

  // 3. VORP over replacement.
  let totalVorp = 0;
  for (const p of scored) {
    p.vorp = Math.max(0, p.pts - (replacement[p.pos] ?? 0));
    totalVorp += p.vorp;
  }

  // 4. Distribute the discretionary pool proportional to VORP, +$1 base.
  for (const p of scored) {
    const share = totalVorp > 0 ? (p.vorp / totalVorp) * pool : 0;
    p.value = Math.max(1, Math.round(1 + share));
  }

  // 5. Suffix-stripped ${name}-${pos} keys.
  const values = {};
  const list = [];
  for (const p of scored) {
    const key = draftValueKey(p.name, p.pos);
    values[key] = p.value;
    list.push({ key, name: p.name, pos: p.pos, team: p.team, pts: p.pts, vorp: p.vorp, value: p.value });
  }
  list.sort((a, b) => b.value - a.value);

  return {
    list,
    values,
    meta: {
      teams,
      budget,
      rosterSize,
      pool,
      scoringLabel: pprLabel(scoring),
      replacement,
      taken: { ...taken },
      totalVorp,
      benchDemand: !!options.benchDemand,
    },
  };
}
